import {
    addDisposer,
    onSnapshot,
    onPatch,
    isStateTreeNode,
} from 'mobx-state-tree';

import Config from '../definitions/Config';
import createDocumentDAO from './createDocumentDao';


// Cache reactions to avoid creating duplicate reactions; 
// we need to return the disposer from `startSavingDocumentChanges` so we'll cache that; indexed by the document id.
// ie. only one reaction per document.
const DISPOSERS = new Map();


/**
 * @param {Object} store sessionStore
 * @param {Object} mstDocument MST instance
 * @param {string} path document path
 * @param {(document: Object) => Object} extractMetadata extract metadata given a snapshot of the document
 *
 * @returns {Function} disposer
 */
function startSavingDocumentChanges(sessionStore, document, path) {
    if (!isStateTreeNode(document)) {
        throw new Error('Cannot actively save changes on an non-MST object. Please call Model.create(...) on the provided object.');
    }

    // Don't re-create the reaction if it already exists
    // Otherwise, we'll get duplicate reactions (and therefore duplicate save requests)
    if (DISPOSERS.has(document.id)) {
        return DISPOSERS.get(document.id);
    }

    const documentDao = getOrCreateStoreReference(sessionStore, document, path);

    // SPECIAL CASE: patching isn't enabled, fall back to POSTing every snapshot.
    if (!Config.ENABLE_DOCUMENT_PATCHING) {
        // We only care about the most-recent POST; when we get multiple, just push the last one.
        const onSnapshotDisposer = onSnapshot(documentDao.document, snapshot => {
            documentDao.enqueuePost(snapshot, snapshot.metadata);
        });
        
        const disposer = addDisposer(documentDao.document, () => {
            onSnapshotDisposer?.();
            DISPOSERS.delete(document.id); // allow us to create a new reaction for this document
        });

        DISPOSERS.set(document.id, disposer);

        return disposer;
    }

    let onSnapshotDisposer, onPatchDisposer;
    // It must be POSTed before we can start PATCHing.
    if (!documentDao.isPersisted) {
        onSnapshotDisposer = onSnapshot(documentDao.document, snapshot => {
            // Swap over to watching for PATCHes.
            onPatchDisposer = startPushingPatches(documentDao);

            // POST our full document then stop watching for full document changes
            documentDao.enqueuePost(snapshot, snapshot.metadata);
            onSnapshotDisposer();
        });
    } else {
        onPatchDisposer = startPushingPatches(documentDao);
    }

    // NOTE: the disposer for all the sub-reactions is added here
    const disposer = addDisposer(documentDao.document, () => {
        onSnapshotDisposer?.();
        onPatchDisposer?.();
        DISPOSERS.delete(document.id); // allow us to create a new reaction for this document
    });

    DISPOSERS.set(document.id, disposer);

    return disposer;
}

function startPushingPatches(documentDao) {
    return onPatch(documentDao.document, patch => {
        // An MST bug appears to occasionally cause malformed patches; can remove once it's fixed
        if (patch.op === 'replace' && typeof patch.value === 'undefined') {
            patch.op = 'remove';
        }

        // Ask the DAO to push the changes to the server.
        documentDao.enqueuePatch(patch, documentDao.document.metadata);
    });
}

function getOrCreateStoreReference(sessionStore, document, path) {
    const existingDocumentDao = sessionStore._documents[document.id];

    if (existingDocumentDao) {
        return existingDocumentDao; // nothing to do
    } else {
        const documentDao = createDocumentDAO({
            id: document.id,
            path,
            metadata: null, // QUESTION - do we want to intialize metadata on DAO creation for non-persisted new document?
            document,
            isPersisted: false,
            sessionStore,
        });
        sessionStore._documents[document.id] = documentDao;

        return documentDao;
    }
}

export default startSavingDocumentChanges;
export { DISPOSERS }; // need to clear between test runs :( eventually this will be removed and we'll store disposer in DAO instead
